import { useSnackbar } from "notistack";
import { useMultiSelect } from "@coherehealth/common";
import { ServiceCase, useSoftDeleteCases, useUnassignCases } from "@coherehealth/qm-api";
import { useMemo } from "react";

function useMultiCaseActions(serviceCases: ServiceCase[]) {
  const { selectedIndexes, handleSelect, handleSelectAll, resetSelection } = useMultiSelect(serviceCases);

  const { mutate: unassignCases, loading: isUnassigning } = useUnassignCases({});
  const { mutate: softDeleteCases, loading: isDeleting } = useSoftDeleteCases({});
  const { enqueueSnackbar } = useSnackbar();

  const handleDeleteSelected = async () => {
    const selectedCases = selectedIndexes.map((index) => serviceCases[index]);
    const caseIds = selectedCases.map((sc) => sc.id).filter(Boolean) as string[];

    try {
      const result = await softDeleteCases(caseIds);
      enqueueSnackbar(`Successfully deleted ${result.deletedCount} ${result.deletedCount === 1 ? "case" : "cases"}`, {
        variant: "success",
      });
      resetSelection();
      return caseIds;
    } catch {
      enqueueSnackbar(`Failed to delete selected ${selectedIndexes.length === 1 ? "case" : "cases"}`, {
        variant: "error",
      });
    }
  };

  const handleUnassignSelected = async () => {
    const selectedCases = selectedIndexes.map((index) => serviceCases[index]);
    const caseIds = selectedCases.map((sc) => sc.id).filter(Boolean) as string[];

    const result = await unassignCases(caseIds);
    if (result) {
      enqueueSnackbar(
        `Successfully unassigned ${result.unassignedCount} ${result.unassignedCount === 1 ? "case" : "cases"}`,
        { variant: "success" }
      );
      return caseIds;
    }

    enqueueSnackbar(`Failed to unassign selected ${selectedIndexes.length === 1 ? "case" : "cases"}`, {
      variant: "error",
    });
    return null;
  };

  const shouldShowOnHoldWarning = useMemo(() => {
    if (!selectedIndexes) {
      return false;
    }
    return selectedIndexes.some((idx) => !!serviceCases[idx]?.activeCaseHold);
  }, [selectedIndexes, serviceCases]);

  return {
    resetSelection,
    selectedIndexes,
    handleSelect,
    handleSelectAll,
    handleDeleteSelected,
    handleUnassignSelected,
    isDeleting,
    isUnassigning,
    shouldShowOnHoldWarning,
  };
}

export default useMultiCaseActions;
